import { groupBy, map } from 'lodash'
import { useMemo, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import FormField from '@/components/FormField'
import Select, { type GroupedOption } from '@/components/Select/Select'
import { AsyncSelect } from '@/components/common/AsyncSelect'
import type { FormFieldType } from '@/types/form-field-type'
import type { IdAndName } from '@/types/id-and-name'
import type { LabelAndValue } from '@/types/label-and-value'

import styles from './SectionFormSelects.module.scss'
import type { SectionFormValues } from '../../hooks/useSectionForm'
import { getCoursesQueryOptions } from '../../queries/sectionsSelects/getCoursesQueryOptions'
import { getCurriculumsQueryOptions } from '../../queries/sectionsSelects/getCurriculumsQueryOptions'
import { useSectionSchoolYears } from '../../queries/sectionsSelects/useSectionSchoolYears'

type SelectName = 'curriculum' | 'schoolYears' | 'course'

const SectionFormSelects = () => {
  const { t } = useTranslation('sections')
  const form = useFormContext<SectionFormValues>()

  const [educationLevelDict, setEducationLevelDict] = useState<
    Record<string, string | undefined>
  >({})

  const setEducationLevel = (name: string, value: string | undefined) =>
    setEducationLevelDict(prev => ({ ...prev, [name]: value }))

  const createChangeHandler =
    (
      name: SelectName,
      inputProps: FormFieldType<LabelAndValueWithMeta>
    ): FormFieldType<LabelAndValueWithMeta>['onChange'] =>
    value => {
      inputProps.onChange?.(value)

      setEducationLevel(name, value.meta?.id)
    }

  const createClearHandler = (name: SelectName) => () => {
    form.resetField(name)
    setEducationLevel(name, undefined)
  }

  const educationLevel = Object.values(educationLevelDict).find(Boolean)
  const curriculumId = form.getValues('curriculum.value')
  const courseId = form.getValues('course.value')

  return (
    <div className={styles.container}>
      <FormField
        id="curriculum"
        name="curriculum"
        label={t('label.curriculum')}
        required
        control={form.control}
        render={({ inputProps }) => (
          <AsyncSelect
            {...inputProps}
            onChange={createChangeHandler('curriculum', inputProps)}
            queryOptions={getCurriculumsQueryOptions(educationLevel, courseId)}
            clearable
            onClear={createClearHandler('curriculum')}
            placeholder={t('placeholder.select-curriculum')}
          />
        )}
      />
      <FormField
        id="schoolYears"
        name="schoolYears"
        label={t('label.school-years')}
        required
        control={form.control}
        render={({ inputProps }) => (
          <SchoolYearsSelect
            {...inputProps}
            educationLevel={educationLevel}
            onChange={createChangeHandler('schoolYears', inputProps)}
            onClear={createClearHandler('schoolYears')}
          />
        )}
      />
      <FormField
        id="course"
        name="course"
        label={t('label.course')}
        required
        control={form.control}
        render={({ inputProps }) => (
          <AsyncSelect
            {...inputProps}
            onChange={createChangeHandler('course', inputProps)}
            queryOptions={getCoursesQueryOptions(educationLevel, curriculumId)}
            clearable
            onClear={createClearHandler('course')}
            placeholder={t('placeholder.select-course')}
          />
        )}
      />
    </div>
  )
}

type FormFieldWithOnClear = FormFieldType<LabelAndValueWithMeta> & {
  onClear: () => void
  educationLevel: string | undefined
}

type LabelAndValueWithMeta = LabelAndValue & { meta?: IdAndName }

const SchoolYearsSelect = (props: FormFieldWithOnClear) => {
  const { t } = useTranslation('sections')

  const { data = [], isLoading } = useSectionSchoolYears(props.educationLevel)

  const groupedOptions: GroupedOption<string>[] = useMemo(
    () =>
      map(groupBy(data, 'meta.name'), (options, label) => ({
        label,
        options
      })),
    [data]
  )

  return (
    <Select
      {...props}
      mode="detailed"
      loading={isLoading}
      options={groupedOptions}
      clearable
      placeholder={t('placeholder.select-school-years')}
    />
  )
}

export default SectionFormSelects
